import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useDirtyNav } from "@/contexts/DirtyNavContext";
import { cn } from "@/lib/utils";

import { tr } from "@/lib/tr";
interface BackButtonProps {
  /** Optional fallback route if there is no history to pop. */
  fallback?: string;
  /** Hide on these exact pathnames (e.g. dashboard / admin root). */
  hideOn?: string[];
  /** Render the icon only (square button). */
  iconOnly?: boolean;
  className?: string;
}

/**
 * Global Back button used in topbars across the app.
 * - Respects RTL (arrow flips direction).
 * - Hooked into `useDirtyNav` so unsaved-form changes trigger a confirmation.
 * - Never shows on root marketing or top-level dashboards by default.
 */
export const BackButton = ({
  fallback,
  hideOn = ["/", "/dashboard", "/admin", "/companies", "/auth"],
  iconOnly = false,
  className,
}: BackButtonProps) => {
  const { i18n } = useTranslation();
  const { pathname } = useLocation();
  const { goBack } = useDirtyNav();
  const isAr = i18n.language === "ar";
  const isRtl = i18n.dir() === "rtl";

  if (hideOn.includes(pathname)) return null;

  const Icon = isRtl ? ArrowRight : ArrowLeft;
  const label = tr("عودة", "Back", "Retour");

  if (iconOnly) {
    return (
      <Button
        type="button"
        variant="outline"
        size="icon"
        aria-label={label}
        onClick={() => goBack(fallback)}
        className={cn("border-s-2 border-s-primary/40 text-muted-foreground hover:text-foreground hover:border-s-primary transition-colors", className)}
      >
        <Icon className="h-5 w-5" />
      </Button>
    );
  }

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={() => goBack(fallback)}
      className={cn("border-s-2 border-s-primary/40 text-muted-foreground hover:text-foreground hover:border-s-primary transition-colors", className)}
    >
      <Icon className="h-4 w-4 me-1.5 shrink-0" />
      {label}
    </Button>
  );
};
